class SbProductGallery extends HTMLElement {
  abort = null;
  emblaBindings = [];

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  destroy() {
    this.abort?.abort();
    this.abort = null;
    this.emblaBindings.forEach(({ embla, evt, fn }) => {
      try {
        embla.off(evt, fn);
      } catch (_) {}
    });
    this.emblaBindings = [];
  }

  async init() {
    if (!this.isConnected) return;
    this.destroy();
    if (Number(this.dataset.totalSlides) < 1) return;

    this.abort = new AbortController();
    const { signal } = this.abort;

    await customElements.whenDefined('carousel-component');
    await this.waitForEmbla();

    const carousel = this.querySelector('carousel-component');
    const embla = carousel?.embla;
    if (!embla) return;

    const onSelect = () => {
      this.syncCounter(embla);
      this.syncSlideA11y(embla);
    };

    embla.on('select', onSelect);
    this.emblaBindings.push({ embla, evt: 'select', fn: onSelect });

    const onReInit = () => {
      onSelect();
    };
    embla.on('reInit', onReInit);
    this.emblaBindings.push({ embla, evt: 'reInit', fn: onReInit });

    document.addEventListener('sb:variant-change', this.onVariantChange, { signal });
    document.addEventListener('shopify:section:load', this.onSectionLoad, { signal });
    window.addEventListener('popstate', this.onPopState, { signal });

    this.scrollToFeaturedMediaId(this.readInitialMediaId(), true);
    onSelect();
  }

  waitForEmbla() {
    return new Promise((resolve) => {
      let frames = 0;
      const max = 120;
      const step = () => {
        const carousel = this.querySelector('carousel-component');
        if (carousel?.embla || frames++ >= max) {
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  readInitialMediaId() {
    const raw = this.dataset.initialFeaturedMediaId;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  scrollToFeaturedMediaId(mediaId, jump) {
    const carousel = this.querySelector('carousel-component');
    const embla = carousel?.embla;
    if (!embla || mediaId == null) return;
    const slides = embla.slideNodes();
    const idx = slides.findIndex((slide) => {
      const id = slide.dataset?.mediaId;
      if (id == null || id === '') return false;
      return Number(id) === Number(mediaId);
    });
    if (idx < 0) return;
    embla.scrollTo(idx, jump);
  }

  onVariantChange = (event) => {
    const variant = event.detail?.variant;
    const id = variant?.featured_media?.id;
    if (id == null) return;
    this.scrollToFeaturedMediaId(Number(id), false);
  };

  onSectionLoad = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.contains(this) || !this.isConnected) return;
    this.init();
  };

  onPopState = () => {
    const carousel = this.querySelector('carousel-component');
    const embla = carousel?.embla;
    if (!embla) return;
    const variantId = new URL(window.location.href).searchParams.get('variant');
    if (!variantId) return;
    const script = document.querySelector('sb-product-form [data-variants-json]');
    if (!script?.textContent) return;
    try {
      const variants = JSON.parse(script.textContent);
      const variant = variants.find((v) => String(v.id) === String(variantId));
      const mid = variant?.featured_media?.id;
      if (mid != null) this.scrollToFeaturedMediaId(Number(mid), true);
    } catch (_) {}
  };

  syncCounter(embla) {
    const total = embla.slideNodes().length;
    const current = embla.selectedScrollSnap() + 1;
    const curEl = this.querySelector('[data-sb-gallery-counter-current]');
    const live = this.querySelector('[data-sb-gallery-live]');
    if (curEl) curEl.textContent = String(current);
    if (live && total > 0) {
      live.textContent = `Image ${current} of ${total}`;
    }
  }

  syncSlideA11y(embla) {
    const selected = embla.selectedScrollSnap();
    embla.slideNodes().forEach((slide, i) => {
      const hidden = i !== selected;
      slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      if ('inert' in slide) slide.inert = hidden;
    });
  }
}

if (!customElements.get('sb-product-gallery')) {
  customElements.define('sb-product-gallery', SbProductGallery);
}
